import React from 'react';
import { motion } from 'framer-motion';
import { FiVideo, FiTrendingUp, FiUsers, FiPlay, FiBarChart, FiAward, FiCheckCircle } from 'react-icons/fi';

const YouTubeMarketingPage: React.FC = () => {
  const features = [
    {
      icon: FiVideo,
      title: 'Channel Optimization',
      description: 'Complete YouTube channel setup and optimization including branding, SEO, and content strategy.',
    },
    {
      icon: FiPlay,
      title: 'Video Production',
      description: 'Professional video creation, editing, and post-production services tailored for YouTube success.',
    },
    {
      icon: FiTrendingUp,
      title: 'YouTube SEO',
      description: 'Optimize titles, descriptions, tags, and thumbnails to rank higher in YouTube search results.',
    },
    {
      icon: FiUsers,
      title: 'Audience Growth',
      description: 'Strategic campaigns to grow subscribers, increase views, and build an engaged community.',
    },
    {
      icon: FiBarChart,
      title: 'Analytics & Insights',
      description: 'Comprehensive YouTube analytics tracking with actionable insights to improve performance.',
    },
    {
      icon: FiAward,
      title: 'Monetization Strategy',
      description: 'Help you achieve monetization requirements and maximize revenue from your YouTube channel.',
    },
  ];

  const whatIsYouTubeMarketing = [
    {
      title: 'Second Largest Search Engine',
      description: 'YouTube is the world\'s second-largest search engine with over 2 billion monthly active users.',
    },
    {
      title: 'Long-Form Content Power',
      description: 'Video content creates deeper engagement and trust, perfect for demonstrating products and building authority.',
    },
    {
      title: 'Multiple Revenue Streams',
      description: 'Monetize through ads, sponsorships, affiliate marketing, and product sales directly from your channel.',
    },
    {
      title: 'Evergreen Traffic',
      description: 'Well-optimized videos continue driving traffic and generating leads for months or years after publishing.',
    },
  ];

  const pricingPlans = [
    {
      name: 'Starter',
      price: '₹20,000',
      period: '/month',
      description: 'Perfect for new YouTube channels',
      features: [
        'Channel setup & branding',
        '4 videos per month (editing only)',
        'Basic YouTube SEO',
        'Thumbnail design',
        'Monthly analytics report',
        'Email support',
      ],
      highlighted: false,
    },
    {
      name: 'Professional',
      price: '₹45,000',
      period: '/month',
      description: 'For growing YouTube channels',
      features: [
        'Complete channel optimization',
        '8 videos per month (production + editing)',
        'Advanced YouTube SEO',
        'Custom thumbnail designs',
        'Audience growth strategies',
        'Weekly analytics reports',
        'Community management',
        'Priority support',
      ],
      highlighted: true,
    },
    {
      name: 'Enterprise',
      price: '₹90,000',
      period: '/month',
      description: 'For serious content creators',
      features: [
        'Multi-channel management',
        '16+ videos per month',
        'Full video production team',
        'YouTube ads campaigns',
        'Influencer collaborations',
        'Monetization optimization',
        'Daily performance monitoring',
        'Brand partnership opportunities',
        'Dedicated account manager',
        '24/7 support',
      ],
      highlighted: false,
    },
  ];

  const whyChooseUs = [
    {
      title: 'YouTube Certified Experts',
      description: 'Our team includes YouTube-certified specialists who understand the platform\'s algorithm and best practices.',
    },
    {
      title: 'Proven Growth Track Record',
      description: 'We\'ve helped channels grow from zero to 100K+ subscribers and achieve monetization within months.',
    },
    {
      title: 'Full-Service Production',
      description: 'From concept to publishing, we handle everything - scripting, filming, editing, optimization, and promotion.',
    },
  ];

  const faqs = [
    {
      question: 'How long does it take to grow a YouTube channel?',
      answer: 'Growth depends on many factors including niche, content quality, and consistency. With our strategies, most channels see significant traction within 3-6 months. Some niches grow faster, while others require patience and persistence.',
    },
    {
      question: 'Do I need expensive equipment to start YouTube?',
      answer: 'Not necessarily. While good equipment helps, content quality and strategy matter more. We help you create professional content with whatever equipment you have, and guide you on cost-effective upgrades as you grow.',
    },
    {
      question: 'How do I get my videos to rank in YouTube search?',
      answer: 'YouTube SEO involves keyword research, optimizing titles and descriptions, creating compelling thumbnails, encouraging engagement, and maintaining watch time. We handle all these aspects to improve your video rankings.',
    },
    {
      question: 'When can I monetize my YouTube channel?',
      answer: 'You need 1,000 subscribers and 4,000 watch hours in the past 12 months to apply for YouTube Partner Program. We implement strategies to help you reach these milestones faster and maximize revenue once monetized.',
    },
    {
      question: 'What type of content performs best on YouTube?',
      answer: 'It varies by niche, but generally how-to videos, tutorials, reviews, entertainment, and educational content perform well. We analyze your niche and audience to create content strategies that resonate with your target viewers.',
    },
    {
      question: 'Should I use YouTube ads to grow my channel?',
      answer: 'YouTube ads can accelerate growth when used strategically. We recommend starting with organic growth and then using ads to amplify your best-performing content and reach monetization requirements faster.',
    },
    {
      question: 'How important are thumbnails and titles?',
      answer: 'Extremely important! They\'re the first things viewers see and directly impact click-through rates. We create eye-catching thumbnails and compelling titles that attract clicks while accurately representing your content.',
    },
    {
      question: 'Can you help with YouTube shorts?',
      answer: 'Absolutely! YouTube Shorts are a powerful way to grow your channel quickly. We create short-form content strategies and help you leverage Shorts to drive subscribers to your long-form content.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-gray-50">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF0000]/5 via-transparent to-[#282828]/5 z-0" />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FF0000] to-[#CC0000] text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
              <FiVideo className="w-4 h-4" />
              YouTube Marketing Services
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-[#FF0000] via-[#CC0000] to-[#282828] bg-clip-text text-transparent leading-tight">
              YouTube Marketing
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed break-words">
              YouTube marketing helps your brand grow using videos, audience targeting, and smart content strategies. It's one of the easiest ways to reach people who are already watching topics they love. Whether you're promoting a service, boosting your brand, or trying to increase sales, YouTube gives you the space to build trust, share value, and stay visible.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-[#FF0000] to-[#CC0000] text-white px-8 py-4 rounded-xl font-semibold hover:shadow-xl transition-all duration-300"
              >
                Start Growing Today
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-[#FF0000] px-8 py-4 rounded-xl font-semibold border-2 border-[#FF0000] hover:bg-[#FF0000] hover:text-white transition-all duration-300"
              >
                View Success Stories
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What is YouTube Marketing Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              What is YouTube Marketing?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Through youtube video promotion, creative ideas, and consistent uploads, your brand can connect with viewers in a natural and meaningful way.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'Content Creation',
                description: 'Short videos, script ideas, and creative concepts tailored to your brand personality and audience interests.',
              },
              {
                title: 'YouTube SEO & Optimization',
                description: 'Optimizing titles, tags, descriptions, thumbnails, and playlists so your videos rank higher and reach more people.',
              },
              {
                title: 'Paid YouTube Video Promotion',
                description: 'Targeted ad campaigns that bring real views, better watch time, and engaged audiences—not fake numbers.',
              },
              {
                title: 'Channel Growth Strategy',
                description: 'A complete plan including posting schedules, content direction, analytics tracking, and consistent management for long-term results.',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-red-50 to-gray-50 p-6 rounded-2xl hover:shadow-lg transition-all duration-300"
              >
                <h3 className="text-xl font-semibold mb-3 text-gray-900 break-words">
                  {item.title}
                </h3>
                <p className="text-gray-600 break-words">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-red-50 to-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Our YouTube Marketing Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              End-to-end YouTube growth solutions from strategy to execution
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-[#FF0000] to-[#CC0000] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-900 break-words">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 break-words">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              YouTube Marketing Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Plans designed to grow your channel and audience
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className={`relative p-8 rounded-2xl transition-all duration-300 ${
                  plan.highlighted
                    ? 'bg-gradient-to-br from-[#FF0000] to-[#CC0000] text-white shadow-2xl scale-105'
                    : 'bg-white border-2 border-gray-200 shadow-lg hover:shadow-2xl'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                <h3
                  className={`text-2xl font-bold mb-2 ${
                    plan.highlighted ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span
                    className={`text-4xl font-bold ${
                      plan.highlighted ? 'text-white' : 'text-[#FF0000]'
                    }`}
                  >
                    {plan.price}
                  </span>
                  <span
                    className={plan.highlighted ? 'text-red-100' : 'text-gray-600'}
                  >
                    {plan.period}
                  </span>
                </div>
                <p
                  className={`mb-6 ${
                    plan.highlighted ? 'text-red-100' : 'text-gray-600'
                  }`}
                >
                  {plan.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <FiCheckCircle
                        className={`w-5 h-5 mr-2 flex-shrink-0 mt-0.5 ${
                          plan.highlighted ? 'text-white' : 'text-[#FF0000]'
                        }`}
                      />
                      <span
                        className={
                          plan.highlighted ? 'text-red-50' : 'text-gray-600'
                        }
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
                    plan.highlighted
                      ? 'bg-white text-[#FF0000] hover:bg-red-50'
                      : 'bg-gradient-to-r from-[#FF0000] to-[#CC0000] text-white hover:shadow-lg'
                  }`}
                >
                  Get Started
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-red-50 to-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Why Choose Us for YouTube Marketing?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              As a trusted YouTube marketing team in India, we focus on simple, effective strategies that feel real, relatable, and easy for your audience to understand. Our goal is not just views — it's genuine engagement and long-term channel growth.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Creating Videos Your Audience Wants',
                description: 'We create videos your audience actually wants to watch and improve your visibility through strong YouTube SEO.',
              },
              {
                title: 'Authentic Video Promotion',
                description: 'Running authentic youtube video promotion campaigns and managing your content schedule with consistency.',
              },
              {
                title: 'Loyal Community Building',
                description: 'Building a loyal community, not just random traffic. We keep things clean, strategic, and aligned with your brand voice—so your channel grows without feeling forced or complicated.',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#FF0000] bg-opacity-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <FiCheckCircle className="w-5 h-5 text-[#FF0000]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-gray-900 break-words">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 break-words">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Your YouTube marketing questions answered
            </p>
          </motion.div>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-red-50 to-gray-50 p-6 rounded-2xl"
              >
                <h3 className="text-lg font-semibold mb-2 text-gray-900 break-words">
                  {faq.question}
                </h3>
                <p className="text-gray-600 break-words">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#FF0000] to-[#CC0000]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6 text-white">
              Ready to Dominate YouTube?
            </h2>
            <p className="text-xl text-red-100 mb-8">
              Let's grow your channel, build your audience, and turn views into revenue
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-[#FF0000] px-8 py-4 rounded-xl font-semibold hover:bg-red-50 transition-all duration-300 shadow-xl"
            >
              Start Your YouTube Journey
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default YouTubeMarketingPage;
